import { Person } from "@/Common/Models/Person/Person";
import { PortfolioInboxes } from "@/Common/Models/UI/Enums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { ApssClient } from "@/Patient/Api/Apss/ApssClient";
import { APSSFormInboxItem } from "@/Portfolio/Models/MyNotesInboxItem";
import { MyAPSSFormTableItem } from "@/Portfolio/Models/MyNotesInboxTableItem";
import { PortfolioStore } from "@/Portfolio/Stores/PortfolioStore";

class PortfolioApssInboxHandler extends BaseOperationHandler {
    constructor() {
        super();

        this.RegisterOperation(LocalOperations.Portfolio.APSS_Reload, this.APSS_Reload, false);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_ToggleOpenCloseSettings, this.APSS_ToggleOpenCloseSettings);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_HideSettings, this.APSS_HideSettings);

        // As of 2025/09/29, APSS counters are not working because SAFIR has shut them down du to issues with their API.
        // They are working on a fix, but for now we disable the automatic update of the counters.
        this.RegisterOperation(LocalOperations.Portfolio.APSS_UpdateApssCount, this.APSS_UpdateApssCount);

        this.RegisterOperation(LocalOperations.Portfolio.APSS_OnShowOnlyMissingRequierementChange, this.APSS_OnShowOnlyMissingRequierementChange);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_OnShowArchivedPurgesChange, this.APSS_OnShowArchivedPurgesChange);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_SearchSettings_ShowColumnOrdering, this.APSS_SearchSettings_ShowColumnOrdering);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_SearchSettings_HideColumnOrdering, this.APSS_SearchSettings_HideColumnOrdering);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_RowClick, this.APSS_RowClick);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_SearchChange, this.APSS_SearchChange);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_PageEvent, this.APSS_PageEvent);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_OnSettingsContainerPerPageChange, this.APSS_OnSettingsContainerPerPageChange);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_ExpandTableItem, this.APSS_ExpandTableItem);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_ConseilNumerique_Reload, this.loadConseilNumerique, false);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_ConseilNumerique_SearchChange, this.conseilNumeriqueSearchChange);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_ConseilNumerique_PageEvent, this.conseilNumeriquePageEvent);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_OnShowArchivedConseilNumerique, this.showArchivedConseilNumeriqueChange);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_SearchSettings_ChangeEmployee, this.changeEmployee);
    }

    private APSS_OnShowOnlyMissingRequierementChange(request: LocalEvent, store: PortfolioStore): void {
        store.apssInboxModel = store.apssInboxModel.set("ShowPrerequisManquant", !store.apssInboxModel.get("ShowPrerequisManquant"));
    }

    private APSS_OnShowArchivedPurgesChange(request: LocalEvent, store: PortfolioStore): void {
        store.apssInboxModel = store.apssInboxModel.set("ShowArchivedPurges", !store.apssInboxModel.get("ShowArchivedPurges"));
    }

    private APSS_HideSettings(request: LocalEvent, store: PortfolioStore): void {
        store.apssInboxModel = store.apssInboxModel.set("ShowSettings", false);
    }

    private APSS_ToggleOpenCloseSettings(request: LocalEvent, store: PortfolioStore): void {
        store.apssInboxModel = store.apssInboxModel.set("ShowSettings", !store.apssInboxModel.get("ShowSettings"));
    }

    private APSS_OnSettingsContainerPerPageChange(request: LocalEvent, store: PortfolioStore): void {
        store.apssInboxModel = store.apssInboxModel
            .set("SelectedPageSizeOption", request.Get<number>("value"))
            .set("ConseilNumeriqueSelectedPageSizeOption", request.Get<number>("value"));
        this.APSS_Reload(request, store);
    }

    private APSS_PageEvent(request: LocalEvent, store: PortfolioStore): void {
        var value = request.Get<number>("Page");
        store.apssInboxModel = store.apssInboxModel.set("CurrentPage", value);
        this.APSS_Reload(request, store);
    }

    private APSS_SearchChange(request: LocalEvent, store: PortfolioStore): void {
        var value = request.Get<string>("value");
        store.apssInboxModel = store.apssInboxModel.set("GlobalPortfolioSearchText", value);
        this.APSS_Reload(request, store);
    }

    private APSS_Reload(request: LocalEvent, store: PortfolioStore): void {
        const inbox = request.Get<PortfolioInboxes>("inboxType") ?? store.Model.get("CurrentInbox");

        // Throtlled
        if (store.timeoutReferenceAPSS !== -1) {
            clearTimeout(store.timeoutReferenceAPSS);
            store.timeoutReferenceAPSS = -1;
        }

        store.apssInboxModel.set("TableData", Immutable.List([]));
        store.timeoutReferenceAPSS = window.setTimeout(async () => {
            try {
                const response = await ApssClient.post(
                    inbox == PortfolioInboxes.APSS_Recent
                        ? ApssClient.getRecentRequests(
                            portfolioStore.apssInboxModel.get("FilterType"),
                            portfolioStore.apssInboxModel.get("SelectedEmployee")?.EmployeeModel,
                            portfolioStore.apssInboxModel.get("CurrentPage"),
                            portfolioStore.apssInboxModel.get("SelectedPageSizeOption"),
                            portfolioStore.apssInboxModel.get("ShowPrerequisManquant"),
                            portfolioStore.apssInboxModel.get("StatusFilter"),
                            portfolioStore.apssInboxModel.get("GlobalPortfolioSearchText")
                        )
                        : ApssClient.getClosedRequestsByMSSSPurge(
                            portfolioStore.apssInboxModel.get("FilterType"),
                            portfolioStore.apssInboxModel.get("SelectedEmployee")?.EmployeeModel,
                            portfolioStore.apssInboxModel.get("CurrentPage"),
                            portfolioStore.apssInboxModel.get("SelectedPageSizeOption"),
                            portfolioStore.apssInboxModel.get("GlobalPortfolioSearchText"),
                            portfolioStore.apssInboxModel.get("ShowArchivedPurges")
                        )
                );

                const requetesRaw = response.Requetes;
                if (requetesRaw != null) {
                    const decodedObject = MyAPSSFormTableItem.DecodeHtmlCaracters<APSSFormInboxItem[]>(requetesRaw);
                    const expendedItem = portfolioStore.apssInboxModel.get("ExpandPrerequis");
                    store.apssInboxModel = store.apssInboxModel.set(
                        "TableData",
                        decodedObject
                            .map(x => {
                                const tableItem = new MyAPSSFormTableItem(x);
                                if (expendedItem != null && tableItem.Model.ReqReg_Id == expendedItem)
                                    tableItem.ExpandPrerequis = true;
                                return tableItem;
                            })
                            .toImmutable()
                    );
                }

                const totalResults = response?.ReqDisplayTotal as number ?? 0; // ReqTotal
                if (totalResults !== 0)
                    store.apssInboxModel = store.apssInboxModel.set("TotalQueryRows", totalResults);

                store.EmitChangeToListeners();
            }
            catch (error) {
                console.error("error reloading APSS", error);
            }
        }, 250);
    }

    private loadConseilNumerique(request: LocalEvent, store: PortfolioStore): void {
        if (store.timeoutReferenceAPSS !== -1) {
            clearTimeout(store.timeoutReferenceAPSS);
            store.timeoutReferenceAPSS = -1;
        }

        store.timeoutReferenceAPSS = window.setTimeout(async () => {
            try {
                const response = await ApssClient.post(ApssClient.fetchAllConseilNumeriqueForms(
                    portfolioStore.apssInboxModel.get("FilterType"),
                    portfolioStore.apssInboxModel.get("SelectedEmployee")?.EmployeeModel,
                    portfolioStore.apssInboxModel.get("ConseilNumeriqueCurrentPage"),
                    portfolioStore.apssInboxModel.get("ConseilNumeriqueSelectedPageSizeOption"),
                    portfolioStore.apssInboxModel.get("GlobalPortfolioSearchText"),
                    portfolioStore.apssInboxModel.get("ShowArchivedConseilNumerique")
                ));

                if (!response?.Requetes)
                    return;

                const requetesRaw = response.Requetes;
                if (requetesRaw != null) {
                    const decodedObject = MyAPSSFormTableItem.DecodeHtmlCaracters<APSSFormInboxItem[]>(requetesRaw);
                    const expendedItem = portfolioStore.apssInboxModel.get("ExpandPrerequis");

                    store.apssInboxModel = store.apssInboxModel.set(
                        "ConseilNumeriqueTableData",
                        decodedObject
                            .map(x => {
                                const tableItem = new MyAPSSFormTableItem(x);
                                if (expendedItem != null && tableItem.Model.ReqReg_Id == expendedItem)
                                    tableItem.ExpandPrerequis = true;
                                return tableItem;
                            })
                            .toImmutable()
                    ).set("ShowSettings", false);

                    const totalResults = response?.ReqDisplayTotal as number ?? 0; // ReqTotal
                    if (totalResults !== 0)
                        store.apssInboxModel = store.apssInboxModel.set("ConseilNumeriqueTotalQueryRows", totalResults);

                    if (cacheStore.ClinicSettings.EnableConseilNumerique) {
                        const countConseilNumeriqueResponse = await ApssClient.post(ApssClient.getAPSSRequestCount(false, true));
                        const conseilNumeriqueCount = countConseilNumeriqueResponse?.ReqDisplayTotal as number ?? 0;
                        if (conseilNumeriqueCount !== 0) {
                            store.Model = store.Model.set("ApssConseilNumeriqueCount", conseilNumeriqueCount);

                            store.PortfolioActions.LoadPortfolioCount();
                        }
                    }

                    store.EmitChangeToListeners();
                }
            }
            catch (error) {
                console.error("error reloading Conseil Numerique forms", error);
            }
        });
    }

    private conseilNumeriqueSearchChange(request: LocalEvent, store: PortfolioStore): void {
        const value = request.Get<string>("value");
        store.apssInboxModel = store.apssInboxModel.set("GlobalPortfolioSearchText", value);
        this.loadConseilNumerique(request, store);
    }

    private APSS_UpdateApssCount(request: LocalEvent, store: PortfolioStore): void {
        return; // Disabled for now, see comment below.

        // for now, we return this operation because it is failed everytime since SAFIR is blocking the call
        // will turn back when it's fixed.

        // throttled
        if (store.timeoutReferenceAPSS !== -1) {
            clearTimeout(store.timeoutReferenceAPSS);
            store.timeoutReferenceAPSS = -1;
        }

        console.log("CRDS and ConseilNumerique count update requested are disabled for now, until SAFIR fixes the issue with their API and provide a real counter.");

        store.timeoutReferenceAPSS = window.setTimeout(async () => {
            try {
                const countRequestResponse = await ApssClient.post(ApssClient.getAPSSRequestCount(false, false));
                const recentCount = countRequestResponse?.ReqDisplayTotal as number ?? 0;
                if (recentCount !== 0)
                    store.Model = store.Model.set("ApssRecentCount", recentCount);

                const countPurgeResponse = await ApssClient.post(ApssClient.getAPSSRequestCount(true, false));
                const purgeCount = countPurgeResponse?.ReqDisplayTotal as number ?? 0;
                if (purgeCount !== 0)
                    store.Model = store.Model.set("ApssPurgeCount", purgeCount);

                const countConseilNumeriqueResponse = await ApssClient.post(ApssClient.getAPSSRequestCount(false, true));
                const conseilNumeriqueCount = countConseilNumeriqueResponse?.ReqDisplayTotal as number ?? 0;
                if (conseilNumeriqueCount != null)
                    store.Model = store.Model.set("ApssConseilNumeriqueCount", conseilNumeriqueCount);

                store.EmitChangeToListeners();
            }
            catch (error) {
                console.error("error updating APSS count", error);
            }
        }, 250);
    }

    private conseilNumeriquePageEvent(request: LocalEvent, store: PortfolioStore): void {
        const value = request.Get<number>("Page");
        store.apssInboxModel = store.apssInboxModel.set("ConseilNumeriqueCurrentPage", value);
        this.loadConseilNumerique(request, store);
    }

    private showArchivedConseilNumeriqueChange(request: LocalEvent, store: PortfolioStore): void {
        store.apssInboxModel = store.apssInboxModel.set("ShowArchivedConseilNumerique", !store.apssInboxModel.get("ShowArchivedConseilNumerique"));
    }

    private APSS_SearchSettings_ShowColumnOrdering(request: LocalEvent, store: PortfolioStore): void {
        store.apssInboxModel = store.apssInboxModel.set("ColumnOrderingPopupOpen", true);
    }

    private APSS_SearchSettings_HideColumnOrdering(request: LocalEvent, store: PortfolioStore): void {
        store.apssInboxModel = store.apssInboxModel.set("ColumnOrderingPopupOpen", false);
    }

    private APSS_RowClick(request: LocalEvent): void {
        var data = request.Get<APSSFormInboxItem>("rowData");

        ApssClient.openForm(data.ReqReg_Id, null); // TODO: temporary solution, same historical Id as the one used to generate de list of forms
    }

    private APSS_ExpandTableItem(request: LocalEvent): void {
        let id = request.Get<string>("id");
        let data = request.Get<Immutable.List<MyAPSSFormTableItem>>("data");

        portfolioStore.apssInboxModel = portfolioStore.apssInboxModel.set("ExpandPrerequis", id);
        portfolioStore.apssInboxModel = portfolioStore.apssInboxModel.set("TableData", data);
        portfolioStore.EmitChangeToListeners();
    }

    private changeEmployee(request: LocalEvent, store: PortfolioStore): void {
        const value = request.Get<Person>("value");
        store.apssInboxModel = store.apssInboxModel.set("SelectedEmployee", value);
    }
}

export { PortfolioApssInboxHandler };
